import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

const stats = [
  { value: '50,000+', label: '学习用户' },
  { value: '24+', label: '精选视频' },
  { value: '8+', label: '深度文章' },
  { value: '5', label: '内容分类' },
];

const testimonials = [
  {
    name: '种植大户老王',
    role: '香菇种植户',
    content: '通过蘑菇成品人视频学习到了很多实用的种植技巧，特别是出菇管理那几期教程，让我家的产量提升了30%以上。强烈推荐给所有蘑菇种植爱好者！',
    avatar: 'https://readdy.ai/api/search-image?query=Middle%20aged%20Chinese%20man%20portrait%2C%20friendly%20smile%2C%20casual%20clothing%2C%20clean%20simple%20background%2C%20professional%20headshot%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-1&orientation=squarish',
  },
  {
    name: '小李爱种植',
    role: '新手爱好者',
    content: '作为一个完全没有种植经验的新手，蘑菇成品人视频的新手入门指南讲得太清楚了！跟着视频一步步操作，两个多月就成功种出了第一批平菇，超有成就感！',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20portrait%2C%20warm%20smile%2C%20casual%20style%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20friendly%20professional%20headshot&width=80&height=80&seq=avatar-2&orientation=squarish',
  },
  {
    name: '农技推广员张工',
    role: '农业技术人员',
    content: '作为农业技术推广人员，我经常向菇农推荐蘑菇成品人视频这个平台。视频内容专业、实用，而且免费观看，是非常好的农业科普资源。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20man%20in%20his%20thirties%20portrait%2C%20professional%20appearance%2C%20confident%20expression%2C%20clean%20simple%20background%2C%20natural%20lighting%2C%20corporate%20headshot%20style&width=80&height=80&seq=avatar-3&orientation=squarish',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9sm0bt8o1vn02ekuhog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch {
        // parsing failed, use default empty object
      }
      const code = typeof parsed?.code === 'string' ? parsed.code : '';

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const errMsg = typeof parsed?.meta?.message === 'string' && parsed.meta.message
          ? parsed.meta.message
          : typeof parsed?.message === 'string' && parsed.message
            ? parsed.message
            : '提交失败，请稍后再试';
        setFormError(errMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <SEO
        title="关于我们"
        description="蘑菇成品人视频精选合集致力于为广大蘑菇种植爱好者提供最全面的蘑菇种植视频教程。了解我们的品牌故事、口碑评价和联系方式。"
        keywords="蘑菇成品人视频,关于我们,蘑菇种植平台,联系我们"
        canonicalUrl="https://mushroom-tutorial.com/about"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于我们 - 蘑菇成品人视频精选合集',
          description: '蘑菇成品人视频精选合集致力于为广大蘑菇种植爱好者提供最全面的蘑菇种植视频教程。',
          url: 'https://mushroom-tutorial.com/about',
        }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                关于我们
              </h1>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="animate-fade-in-up">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">蘑菇成品人视频精选合集</strong>是专注于蘑菇种植视频教程的一站式学习平台。我们汇聚了来自全国各地菇农和种植专家的实战经验，通过高清视频形式，系统化展示蘑菇从菌种培育到成品采收的完整流程。
                </p>
                <p>
                  无论你是刚刚接触蘑菇种植的新手，还是有着多年经验的专业种植者，在这里都能找到适合你的蘑菇成品人视频教程资源。我们的内容涵盖<strong>种植全流程、成品展示、人工培育、制作技巧和采收技术</strong>五大方向，全方位满足不同层次的学习需求。
                </p>
                <p>
                  我们深知蘑菇种植是一门需要实践的技术活。因此，每一期蘑菇成品人视频都注重实操性，力求让观众"看得懂、学得会、用得上"。从培养基配制到出菇管理，从病虫害防治到采后处理，每一个细节都有详细的视频讲解。
                </p>
                <p>
                  蘑菇成品人视频精选合集将持续更新优质内容，努力打造成国内最全面、最实用的蘑菇种植视频教程平台，让更多人享受到蘑菇种植的乐趣与收获。
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 animate-fade-in-up">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="font-heading text-2xl md:text-3xl text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="animate-fade-in-up">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8 max-w-lg mx-auto">
                听听蘑菇成品人视频用户怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-lg p-6 border border-background-200/70 animate-fade-in-up"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-heading text-sm text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-0.5 mt-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 animate-fade-in-up">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                如果你有任何关于蘑菇成品人视频的问题或建议，欢迎与我们联系
              </p>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70 animate-fade-in-up">
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  name="phone_alt"
                  type="text"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200 text-sm text-secondary-800 text-center">
                    <i className="ri-checkbox-circle-line mr-1.5"></i>
                    感谢您的留言！我们会尽快与您联系。
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="p-4 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-800 text-center">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}